import {
  CREATE_OR_UPDATE_ROUTINE_TOOL_NAMES,
  EVENT_TRIGGER_ROUTINE_TOOL_NAMES,
  readToolResultText,
  SCHEDULE_OR_TRIGGER_ROUTINE_TOOL_NAMES,
  type ToolUseItem,
  WORKFLOW_KEYS,
} from '@heidi/agents/contracts';
import { GET_PATIENT_DOCUMENTS, GET_PATIENT_PROFILES } from '@heidi/patients/contracts';
import {
  GET_CONSULT_NOTES,
  GET_DOCUMENT_BY_ID,
  GET_DOCUMENTS_BY_ID,
  GET_SESSION_BY_ID,
  GET_SESSION_CONTEXT_FILES,
} from '@heidi/session/contracts';
import { invalidateClinicalTaskQueries } from '@heidi/tasks/data';
import {
  GET_TEMPLATE_BY_ID,
  GET_TEMPLATE_CREATION_RECORD_BY_TEMPLATE_ID,
} from '@heidi/templates/contracts';
import { invalidateAllTemplatesQuery } from '@heidi/templates/data';
import type { QueryClient } from '@tanstack/react-query';
import { canonicalToolId } from '../../voice-assistant/toolIdentity';

/** Agent registry tool names grouped by the caches their writes affect. */
export const SESSION_DOCUMENT_WRITE_TOOLS = [
  'create_session_document',
  'edit_session_document',
  'rename_session_document',
  'generate_note_from_template',
  'generate_prechart_document',
] as const;

export const PATIENT_PROFILE_WRITE_TOOLS = [
  'create_patient_profile',
  'update_patient_context',
  'merge_patient_profiles',
  'delete_patient_profiles',
  'upload_patient_document',
] as const;

export const CONSULT_NOTE_WRITE_TOOLS = [
  'edit_consult_note',
  'generate_consult_note',
] as const;

export const SESSION_CONTEXT_NOTE_WRITE_TOOLS = [
  'edit_session_context',
  'edit_session_clinician_notes',
  'add_session_context_text',
] as const;

export const SESSION_CONTEXT_DOCUMENT_WRITE_TOOLS = [
  'create_session_context_document',
  'update_session_context_document',
  'delete_session_context_document',
] as const;

export const SESSION_META_WRITE_TOOLS = [
  'create_session',
  'create_session_for_member',
  'set_session_title',
  'set_session_status',
  'link_session_to_patient',
  'update_session_time',
  'delete_session',
  // Also deletes or unlinks the deleted patients' sessions.
  'delete_patient_profiles',
  'merge_sessions',
  'unlink_merged_sessions',
] as const;

export const TEMPLATE_LIBRARY_WRITE_TOOLS = [
  'create_template',
  'favorite_template',
  'pin_template',
  'unpin_template',
  'delete_template',
  'update_template',
  'edit_template_section',
] as const;

/** Exported for unit tests — keep in sync with BE clinical write tool registry. */
export const CLINICAL_TASK_WRITE_TOOLS = [
  'create_clinical_task',
  'update_task_status',
  'bulk_update_clinical_task_status',
  'bulk_delete_clinical_tasks',
  'delete_clinical_task',
  'delete_session_clinical_tasks',
  'assign_clinical_task',
  'unassign_clinical_task',
  'link_clinical_task',
  'unlink_clinical_task',
  'generate_session_clinical_tasks',
] as const;

/** Back-compat alias for voice tests. */
export const CLINICAL_TASK_WRITE_TOOL_KEYS = CLINICAL_TASK_WRITE_TOOLS;

const toIdSet = (names: readonly string[]): Set<string> =>
  new Set(names.map(canonicalToolId));

const SESSION_DOCUMENT_WRITE_TOOL_IDS = toIdSet(SESSION_DOCUMENT_WRITE_TOOLS);
const PATIENT_PROFILE_WRITE_TOOL_IDS = toIdSet(PATIENT_PROFILE_WRITE_TOOLS);
const CONSULT_NOTE_WRITE_TOOL_IDS = toIdSet(CONSULT_NOTE_WRITE_TOOLS);
const SESSION_CONTEXT_NOTE_WRITE_TOOL_IDS = toIdSet(SESSION_CONTEXT_NOTE_WRITE_TOOLS);
const SESSION_CONTEXT_DOCUMENT_WRITE_TOOL_IDS = toIdSet(
  SESSION_CONTEXT_DOCUMENT_WRITE_TOOLS
);
const SESSION_META_WRITE_TOOL_IDS = toIdSet(SESSION_META_WRITE_TOOLS);
const TEMPLATE_LIBRARY_WRITE_TOOL_IDS = toIdSet(TEMPLATE_LIBRARY_WRITE_TOOLS);
const CLINICAL_TASK_WRITE_TOOL_IDS = toIdSet(CLINICAL_TASK_WRITE_TOOLS);
const WORKFLOW_WRITE_TOOL_IDS = toIdSet([...CREATE_OR_UPDATE_ROUTINE_TOOL_NAMES]);
const SCHEDULE_OR_TRIGGER_TOOL_IDS = toIdSet([...SCHEDULE_OR_TRIGGER_ROUTINE_TOOL_NAMES]);
const EVENT_TRIGGER_TOOL_IDS = toIdSet([...EVENT_TRIGGER_ROUTINE_TOOL_NAMES]);

const CREATE_TOOL_NAMES_WITH_RESULT_ID = new Set(
  ['create_session', 'create_session_for_member'].map(canonicalToolId)
);

export type AgentToolCacheInvalidationArgs = {
  queryClient: QueryClient;
  toolName: string;
  sessionId?: string | null;
  patientProfileId?: string | null;
  documentId?: string | null;
  workflowId?: string | null;
  templateId?: string | null;
  onSessionsChanged?: () => void;
  invalidateSessionsList?: () => void;
};

const readToolInputSessionId = (input: Record<string, unknown>): string | null => {
  const id = input.session_id;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

const readToolInputPatientProfileId = (input: Record<string, unknown>): string | null => {
  const id = input.patient_profile_id;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

const readToolInputDocumentId = (input: Record<string, unknown>): string | null => {
  const id = input.document_id;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

const readToolInputWorkflowId = (input: Record<string, unknown>): string | null => {
  const id = input.workflow_id;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

/** Matches artifactFromTool `firstIdToken` — plain-text create_session confirmations. */
const firstIdToken = (text: string | undefined): string | null => {
  if (!text) return null;
  const match = text.match(/\b[a-f0-9]{16,}\b/i);
  return match ? match[0] : null;
};

const readToolResultSessionId = (item: ToolUseItem): string | null => {
  if (!CREATE_TOOL_NAMES_WITH_RESULT_ID.has(canonicalToolId(item.name))) return null;
  const resultText = readToolResultText(item);
  if (!resultText) return null;
  try {
    const parsed = JSON.parse(resultText) as { session_id?: unknown };
    if (typeof parsed.session_id === 'string' && parsed.session_id.length > 0) {
      return parsed.session_id;
    }
  } catch {
    // Not JSON — fall through to id-shaped token scan (parity with canvas deriver).
  }
  return firstIdToken(resultText);
};

const readToolResultPatientProfileId = (item: ToolUseItem): string | null => {
  const resultText = readToolResultText(item);
  if (!resultText) return null;
  try {
    const parsed = JSON.parse(resultText) as { patient_profile_id?: unknown };
    return typeof parsed.patient_profile_id === 'string' &&
      parsed.patient_profile_id.length > 0
      ? parsed.patient_profile_id
      : null;
  } catch {
    return null;
  }
};

const readTemplateId = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

/** Only successful library writes carry a template-detail refresh target. */
const readToolTemplateId = (item: ToolUseItem): string | null => {
  if (
    !TEMPLATE_LIBRARY_WRITE_TOOL_IDS.has(canonicalToolId(item.name)) ||
    item.status !== 'complete' ||
    item.result?.is_error
  ) {
    return null;
  }

  const inputId = readTemplateId(item.input.template_id);
  if (inputId) return inputId;

  const metadata = item.result_provider_metadata?.data;
  if (typeof metadata === 'object' && metadata !== null) {
    const metadataId = readTemplateId((metadata as Record<string, unknown>).template_id);
    if (metadataId) return metadataId;
  }

  const resultText = readToolResultText(item);
  if (!resultText) return null;
  try {
    const parsed: unknown = JSON.parse(resultText);
    return typeof parsed === 'object' && parsed !== null
      ? readTemplateId((parsed as Record<string, unknown>).template_id)
      : null;
  } catch {
    return null;
  }
};

export const resolveAgentToolCacheContext = (
  item: ToolUseItem
): {
  sessionId: string | null;
  patientProfileId: string | null;
  documentId: string | null;
  workflowId: string | null;
  templateId: string | null;
} => ({
  sessionId: readToolInputSessionId(item.input) ?? readToolResultSessionId(item),
  patientProfileId:
    readToolInputPatientProfileId(item.input) ?? readToolResultPatientProfileId(item),
  documentId: readToolInputDocumentId(item.input),
  workflowId: readToolInputWorkflowId(item.input),
  templateId: readToolTemplateId(item),
});

export const isAgentToolWriteRequiringCacheRefresh = (toolName: string): boolean => {
  const id = canonicalToolId(toolName);
  return (
    SESSION_DOCUMENT_WRITE_TOOL_IDS.has(id) ||
    PATIENT_PROFILE_WRITE_TOOL_IDS.has(id) ||
    CONSULT_NOTE_WRITE_TOOL_IDS.has(id) ||
    SESSION_CONTEXT_NOTE_WRITE_TOOL_IDS.has(id) ||
    SESSION_CONTEXT_DOCUMENT_WRITE_TOOL_IDS.has(id) ||
    SESSION_META_WRITE_TOOL_IDS.has(id) ||
    TEMPLATE_LIBRARY_WRITE_TOOL_IDS.has(id) ||
    CLINICAL_TASK_WRITE_TOOL_IDS.has(id) ||
    WORKFLOW_WRITE_TOOL_IDS.has(id) ||
    SCHEDULE_OR_TRIGGER_TOOL_IDS.has(id)
  );
};

export const invalidateCachesForAgentToolWrite = ({
  queryClient,
  toolName,
  sessionId,
  patientProfileId,
  documentId,
  workflowId,
  templateId,
  onSessionsChanged,
  invalidateSessionsList,
}: AgentToolCacheInvalidationArgs): boolean => {
  if (!isAgentToolWriteRequiringCacheRefresh(toolName)) return false;

  const id = canonicalToolId(toolName);

  if (SESSION_DOCUMENT_WRITE_TOOL_IDS.has(id) && sessionId) {
    void queryClient.invalidateQueries({
      queryKey: [GET_DOCUMENTS_BY_ID, { sessionId }],
      refetchType: 'all',
    });
    if (documentId) {
      void queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
        refetchType: 'all',
      });
    }
  }

  if (PATIENT_PROFILE_WRITE_TOOL_IDS.has(id)) {
    void queryClient.invalidateQueries({ queryKey: [GET_PATIENT_PROFILES] });
    if (id === canonicalToolId('upload_patient_document') && patientProfileId) {
      void queryClient.invalidateQueries({
        queryKey: [GET_PATIENT_DOCUMENTS, { patientProfileId }],
      });
    }
  }

  if (CONSULT_NOTE_WRITE_TOOL_IDS.has(id) && sessionId) {
    void queryClient.invalidateQueries({
      queryKey: [GET_SESSION_BY_ID, { sessionId }],
    });
    void queryClient.invalidateQueries({
      queryKey: [GET_CONSULT_NOTES, { sessionId }],
    });
  }

  if (SESSION_CONTEXT_NOTE_WRITE_TOOL_IDS.has(id) && sessionId) {
    void queryClient.invalidateQueries({
      queryKey: [GET_SESSION_BY_ID, { sessionId }],
    });
  }

  if (SESSION_CONTEXT_DOCUMENT_WRITE_TOOL_IDS.has(id) && sessionId) {
    void queryClient.invalidateQueries({
      queryKey: [GET_SESSION_CONTEXT_FILES, { sessionId }],
    });
  }

  if (SESSION_META_WRITE_TOOL_IDS.has(id)) {
    if (sessionId) {
      void queryClient.invalidateQueries({
        queryKey: [GET_SESSION_BY_ID, { sessionId }],
      });
    }
    onSessionsChanged?.();
    invalidateSessionsList?.();
  }

  if (TEMPLATE_LIBRARY_WRITE_TOOL_IDS.has(id)) {
    void invalidateAllTemplatesQuery();
    if (templateId) {
      // Prefix matching includes every generation-method variant of the saved template.
      void queryClient.invalidateQueries({
        queryKey: [GET_TEMPLATE_BY_ID, { templateId }],
      });
      void queryClient.invalidateQueries({
        queryKey: [GET_TEMPLATE_CREATION_RECORD_BY_TEMPLATE_ID, { templateId }],
      });
    }
  }

  if (CLINICAL_TASK_WRITE_TOOL_IDS.has(id)) {
    // Heidi's update_task_status input is { task_id, status } — no session_id —
    // so the session Tasks card would stay stale if we gated on that field.
    invalidateClinicalTaskQueries(queryClient);
  }

  if (WORKFLOW_WRITE_TOOL_IDS.has(id)) {
    void queryClient.invalidateQueries({ queryKey: WORKFLOW_KEYS.all });
  }

  if (SCHEDULE_OR_TRIGGER_TOOL_IDS.has(id) && workflowId) {
    void queryClient.invalidateQueries({
      queryKey: WORKFLOW_KEYS.schedule(workflowId),
      refetchType: 'all',
    });
    // The sidebar reads the batch key, not the per-id schedule cache.
    void queryClient.invalidateQueries({
      queryKey: ['workflows', 'schedules-batch'],
    });
    if (EVENT_TRIGGER_TOOL_IDS.has(id)) {
      void queryClient.invalidateQueries({
        queryKey: WORKFLOW_KEYS.eventRules(workflowId),
        refetchType: 'all',
      });
      void queryClient.invalidateQueries({
        queryKey: ['workflows', 'event-rules-batch'],
      });
    }
  }

  return true;
};
